import { Checkbox, Input } from '@ui/themed';
import { t } from 'i18next';
import z from 'zod';
import { CredentialField } from '../../../../components/credential-field';
import type { Setting } from '../../../../context/settings-context';
import { useSettings } from '../../../../context/settings-context';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { useAppForm } from '../../../../lib/app-form';
import { PARTNER_DOCS_URLS } from '../../constants';
import type { PartnerModalProps } from '../../types';
import { COMMON, PartnerModal, saveAction } from '../partner-modal';
import { credentialValidator, usePartnerModalSave } from './shared';

const CIN7 = 'features:partners.components.cin7' satisfies I18nPrefix;

export function Cin7Modal({ isOpen, onClose }: PartnerModalProps) {
  const settings = useSettings();
  const { save, isSaving } = usePartnerModalSave(onClose);

  const hasStoredApiKey = !!settings.integrationCin7ApiApplicationKey;

  // Settings the API has never persisted come back `undefined`, so each one needs a fallback
  // before it seeds a controlled input — and before the `onSubmit` schema rejects it.
  const form = useAppForm({
    defaultValues: {
      apiAccountId: settings.integrationCin7ApiAccountId ?? '',
      apiApplicationKey: '',
      customerTag: settings.integrationCin7CustomerTag ?? '',
      additionalTags: settings.integrationCin7AdditionalTags ?? '',
      exemptTaxCodes: settings.integrationCin7CustomerExemptTaxCodes ?? '',
      sendInvite: settings.integrationCin7SendInvite ?? false,
      disableChildCustomers: settings.integrationCin7DisableChildCustomers ?? false,
    },
    // Only the credentials are mandatory: the tag and tax-code fields are filters, and merchants
    // who don't use them have always been able to save the integration with them empty.
    validators: {
      onSubmit: z.object({
        apiAccountId: z.string().trim().min(1),
        apiApplicationKey: credentialValidator(hasStoredApiKey),
        customerTag: z.string(),
        additionalTags: z.string(),
        exemptTaxCodes: z.string(),
        sendInvite: z.boolean(),
        disableChildCustomers: z.boolean(),
      }),
    },
    onSubmit: ({ value }) => {
      const payload: Setting[] = [
        { key: 'integrationCin7ApiAccountId', value: value.apiAccountId },
        { key: 'integrationCin7', value: true },
        { key: 'integrationCin7CustomerTag', value: value.customerTag },
        { key: 'integrationCin7AdditionalTags', value: value.additionalTags },
        { key: 'integrationCin7CustomerExemptTaxCodes', value: value.exemptTaxCodes },
        { key: 'integrationCin7SendInvite', value: value.sendInvite },
        { key: 'integrationCin7DisableChildCustomers', value: value.disableChildCustomers },
      ];
      const apiApplicationKey = value.apiApplicationKey.trim();
      if (apiApplicationKey) {
        payload.push({ key: 'integrationCin7ApiApplicationKey', value: apiApplicationKey });
      }
      void save(payload);
    },
  });

  return (
    <PartnerModal
      isOpen={isOpen}
      onClose={onClose}
      title="Cin7 Core"
      description={t(`${CIN7}.description`)}
      docsUrl={PARTNER_DOCS_URLS.cin7}
      primaryAction={saveAction(() => void form.handleSubmit(), isSaving)}
    >
      <form.Field name="apiAccountId">
        {(field) => (
          <Input
            label={t(`${CIN7}.account_id.label`)}
            placeholder={t(`${CIN7}.account_id.placeholder`)}
            tooltip={t(`${CIN7}.account_id.tooltip`)}
            value={field.state.value}
            onChange={field.handleChange}
            error={field.state.meta.errors[0]?.message}
          />
        )}
      </form.Field>
      <form.Field name="apiApplicationKey">
        {(field) => (
          <CredentialField
            label={t(`${CIN7}.api_key.label`)}
            placeholder={t(`${CIN7}.api_key.placeholder`)}
            tooltip={t(`${CIN7}.api_key.tooltip`)}
            value={field.state.value}
            onChange={field.handleChange}
            hasStoredValue={hasStoredApiKey}
            error={field.state.meta.errors[0]?.message}
          />
        )}
      </form.Field>
      <form.Field name="customerTag">
        {(field) => (
          <Input
            label={t(`${CIN7}.customer_tag.label`)}
            placeholder={t(`${CIN7}.customer_tag.placeholder`)}
            description={t(`${CIN7}.customer_tag.description`)}
            value={field.state.value}
            onChange={field.handleChange}
            error={field.state.meta.errors[0]?.message}
          />
        )}
      </form.Field>
      <form.Field name="additionalTags">
        {(field) => (
          <Input
            label={t(`${CIN7}.additional_tags.label`)}
            placeholder={t(`${CIN7}.additional_tags.placeholder`)}
            description={t(`${CIN7}.additional_tags.description`)}
            value={field.state.value}
            onChange={field.handleChange}
            error={field.state.meta.errors[0]?.message}
          />
        )}
      </form.Field>
      <form.Field name="exemptTaxCodes">
        {(field) => (
          <Input
            label={t(`${CIN7}.exempt_codes.label`)}
            placeholder={t(`${CIN7}.exempt_codes.placeholder`)}
            tooltip={t(`${CIN7}.exempt_codes.tooltip`)}
            value={field.state.value}
            onChange={field.handleChange}
            error={field.state.meta.errors[0]?.message}
          />
        )}
      </form.Field>
      <form.Field name="sendInvite">
        {(field) => (
          <Checkbox
            name="sendInviteEmail"
            label={t(`${CIN7}.send_invite_label`)}
            description={t(`${COMMON}.send_invite_description`)}
            checked={field.state.value}
            onChange={() => field.handleChange(!field.state.value)}
          />
        )}
      </form.Field>
      <form.Field name="disableChildCustomers">
        {(field) => (
          <Checkbox
            name="disableChildCustomers"
            label={t(`${CIN7}.disable_child.label`)}
            description={t(`${CIN7}.disable_child.description`)}
            checked={field.state.value}
            onChange={() => field.handleChange(!field.state.value)}
          />
        )}
      </form.Field>
    </PartnerModal>
  );
}
